import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import interventionService from '../../../services/interventionService';
import localService from '../../../services/localService';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';

const EntretiensDetailsModal = ({ isOpen, article, entretiens, onClose }) => {
  const { isDarkMode } = useTheme();
  if (!isOpen || !article) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-2 sm:p-4">
      <div className={`${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[95vh] sm:max-h-[90vh]`}>
        <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}> 
          <h2 className="text-lg sm:text-xl font-bold tracking-wide flex items-center gap-2">
            <i className="fas fa-tools text-primary-500"></i> 
            <span className="hidden sm:inline">Détails des entretiens</span>
            <span className="sm:hidden">Entretiens</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-primary-600 text-xl sm:text-2xl transition-colors">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
          <div className="mb-4 flex flex-col gap-1">
            <span className="font-semibold text-sm sm:text-base">
              Article : <span className="font-mono text-primary-600 dark:text-primary-400 break-all">{article.designation}</span>
            </span>
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Code barre : <span className="font-mono break-all">{article.codeABarre}</span>
            </span>
          </div>
          <div className={`rounded-xl sm:rounded-2xl overflow-hidden border shadow ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider">Entretien</th>
                  </tr>
                </thead>
                <tbody>
                  {entretiens && entretiens.length > 0 ? (
                    entretiens.map((e, idx) => (
                      <tr key={idx} className={isDarkMode ? 'hover:bg-gray-900 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                        <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm break-words">{e.designation}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td className="px-3 sm:px-6 py-3 sm:py-4 text-gray-400 text-center text-sm sm:text-base">Aucun entretien</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InterventionViewModal = ({ isOpen, interventionId, onClose }) => {
  const { isDarkMode } = useTheme();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [intervention, setIntervention] = useState(null);
  const [locals, setLocals] = useState([]);
  const [selectedLocal, setSelectedLocal] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      if (isOpen && interventionId) {
        setLoading(true);
        try {
          const interventionRes = await interventionService.getInterventionById(interventionId);
          if (!cancelled && interventionRes.success) {
            setIntervention(interventionRes.data);
            // Fetch locals for this client only
            const clientId = interventionRes.data.client?.id;
            if (clientId) {
              const res = await localService.getLocauxByClient(clientId);
              let localList = Array.isArray(res.data.list) ? res.data.list : Array.isArray(res.data) ? res.data : [];
              if (!cancelled && res.success) setLocals(localList);
            } else {
              setLocals([]);
            }
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else if (!isOpen) {
        setIntervention(null);
        setLocals([]);
        setSelectedLocal('');
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [isOpen, interventionId]);

  if (!isOpen) return null;
  if (loading || !intervention) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4">
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md sm:max-w-3xl flex flex-col items-center justify-center p-8 sm:p-12`}>
          <div className="text-base sm:text-lg font-semibold flex items-center gap-3">
            <span className="animate-spin fas fa-spinner"></span> Chargement...
          </div>
        </div>
      </div>
    );
  }

  // Prepare rows from entretiens, filter by selected local if set
  const rows = (intervention.entretiens || [])
    .filter(ent => !selectedLocal || ent.article?.localId === Number(selectedLocal))
    .map(ent => {
      const article = ent.article;
      return {
        code: article?.codeABarre || '',
        type: article?.typeArticle?.designation || '',
        article: article?.designation || '',
        capacite: article?.capacite || '',
        details: ent.pieceDeRechangeEtServices || [],
        articleObj: article,
      };
    });

  return (
    <>
      {showDetails && selectedArticle && (
        <EntretiensDetailsModal
          isOpen={showDetails}
          article={selectedArticle.articleObj}
          entretiens={selectedArticle.details}
          onClose={() => setShowDetails(false)}
        />
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4">
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[95vh] sm:max-h-[90vh]`}>
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b flex items-center justify-between flex-shrink-0">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-bold truncate">Intervention</h2>
              <div className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <span className="block sm:inline">
                  Type : {intervention.typeIntervention != 1 ? 'Curative' : 'Préventive'}
                </span>
                {intervention.client && (
                  <span className="block sm:inline sm:ml-2">
                    <span className="hidden sm:inline">- </span>
                    <span className="font-semibold truncate">{intervention.client.raisonSocial}</span>
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="ml-4 text-gray-500 hover:text-primary-600 text-lg sm:text-xl flex-shrink-0">
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          {/* Content (scrollable) */}
          <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
            {/* Filter Section */}
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <label className="font-medium text-sm sm:text-base whitespace-nowrap">Filtrer par local :</label>
              <div className="w-full sm:min-w-[250px] sm:max-w-xs">
                <LocalSelectDropdown
                  value={selectedLocal}
                  onChange={val => setSelectedLocal(val)}
                  options={locals}
                  loading={loading}
                  disabled={locals.length === 0}
                  isDarkMode={isDarkMode}
                  placeholder="Tous les locaux"
                  required={false}
                />
              </div>
            </div>
            
            {/* Table Section */}
            <div className={`rounded-xl sm:rounded-2xl overflow-hidden border shadow-lg ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className={isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider">Code</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Type</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider">Article</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Capacité</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-semibold uppercase tracking-wider">Détails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={idx} className={isDarkMode ? 'hover:bg-gray-800 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                        <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-mono break-all">{row.code}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm hidden sm:table-cell break-words">{row.type}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold break-words">
                          {row.article}
                          {/* Show type on mobile */}
                          <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {row.type}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm hidden md:table-cell">{row.capacite}</td>
                        <td className="px-3 sm:px-6 py-2 sm:py-3">
                          <button
                            className="bg-primary-600 hover:bg-primary-700 text-white rounded-lg px-2 sm:px-3 py-1 sm:py-2 text-sm sm:text-lg shadow-md focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
                            onClick={() => { setSelectedArticle(row); setShowDetails(true); }}
                            title="Voir les entretiens"
                          >
                            <i className="fas fa-eye"></i>
                            <span className="ml-1 sm:hidden text-xs">Voir</span>
                          </button>
                          {/* Show capacity on mobile */}
                          {row.capacite && (
                            <div className="md:hidden text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Capacité: {row.capacite}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-gray-400 py-4 sm:py-6 text-sm sm:text-base">
                          Aucun article
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InterventionViewModal;